import { GetStaticProps, NextPage } from 'next'
import { Dispatch, SetStateAction, useState } from 'react'
import { FaCrown } from 'react-icons/fa'
import { RankingArticleList } from 'web_core/assets/article'
import { LoadingSpinner } from 'web_core/assets/spinners'
import { ESArticle } from 'web_core/elastic_search'
import { useESArticles } from 'web_core/hooks'
import { Serialized, deserializeArray } from 'web_core/utils'
import { DefaultLayout } from '~/components/Layout/Default'
import { ContentNavSelector } from '~/components/Utils/ContentNavSelector'
import { Heading } from '~/components/Utils/Heading'
import { RankingMonthSelector } from '~/components/Utils/RankingMonthSelector'
import { SearchFilter } from '~/components/Utils/SearchFilter'
import { useLayoutProperty } from '~/context/layout_property'
import { LayoutStaticProps } from '~/static_props/layout'
import { getArticlesRankingStaticProps } from '~/static_props/ranking'
import { articleRankingBreadcrumb } from '~/utils'

type Props = {
  articles: Serialized<ESArticle>[]
} & LayoutStaticProps

export const getStaticProps: GetStaticProps<Props> = async ({
  locale = 'ja',
}) => {
  return getArticlesRankingStaticProps(locale)
}

type ContentsMenuProps = {
  categoryId: string | undefined
  setCategoryId: Dispatch<SetStateAction<string | undefined>>
}

const ContentsMenu = ({ categoryId, setCategoryId }: ContentsMenuProps) => {
  const { headerHeight } = useLayoutProperty()
  return (
    <div
      className="position-sticky bg-white pt-2 pb-2 mb-2 mb-md-4"
      style={{ top: headerHeight }}
    >
      <ContentNavSelector
        active="articles"
        link="/ranking/[contents]"
        className="mb-2 mb-md-4"
      />
      <div className="d-flex justify-content-between gap-4">
        <RankingMonthSelector type="articles" className="py-1" />
        <SearchFilter
          className="fs-7 fs-md-6"
          categoryFilter={{ onSelectId: setCategoryId, id: categoryId }}
        />
      </div>
    </div>
  )
}

const Page: NextPage<Props> = ({
  articles: staticArticles,
  categories,
  popularProfiles,
}) => {
  const [categoryId, setCategoryId] = useState<string>()
  const { articles } = useESArticles(
    {
      type: 'ranking',
      locale: 'ja',
      category_id: categoryId,
    },
    deserializeArray(staticArticles, {})
  )

  return (
    <DefaultLayout
      head={{
        title: '歴代記事ランキング',
        description:
          '歴代でユーザからの高評価が多かった上位20記事をご覧いただけます。',
        keyword: 'ランキング',
      }}
      navLinks={{ pc: true, tablet: true, sp: true, active: 'ranking' }}
      categoriesMenu={{ pc: true, sp: true }}
      staticCategories={categories}
      staticPopularProfiles={popularProfiles}
      leftSidebar
      rightSideBar
    >
      {articleRankingBreadcrumb.toReactNode()}
      <Heading title="ランキング" icon={FaCrown} className="mb-0" />
      <ContentsMenu categoryId={categoryId} setCategoryId={setCategoryId} />
      {articles ? (
        <RankingArticleList
          articles={articles}
          className="grid g-cols-1 g-xl-cols-2"
        />
      ) : (
        <LoadingSpinner />
      )}
    </DefaultLayout>
  )
}

export default Page
